import DeleteIcon from '@mui/icons-material/Delete'
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material'
import { useCallback } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  useGetAllInventoryPartsQuery,
  useUpdateInventoryInstanceMutation
} from '../../../redux/api'
import { CounterSaleExpanded, InventoryPartInstanceEntry } from '../../../sdk'
import { formatCurrency, formatStringToTwoDecimalPlaces } from '../../../utils'
import CounterSaleEditInventoryInCart from './CounterSaleEditInventoryInCart'

interface Props {
  counterSale: CounterSaleExpanded | undefined
  readOnly: boolean
}

const CounterSaleCartInventory = ({ counterSale, readOnly }: Props) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const partIds = Array.from(
    new Set(
      (counterSale?.pricing?.inventoryPartEntries || [])
        .map((entry) => entry.info?.inventoryPartId)
        .filter((id) => id !== undefined)
    )
  ) as string[]
  const { data: allShopInventory } = useGetAllInventoryPartsQuery(
    {
      authorization: accessToken,
      shop: shopId,
      stockedOnly: false,
      isFluidsOnly: false,
      partId: partIds
    },
    { skip: partIds.length === 0 }
  )
  const [updateInventoryInstance] = useUpdateInventoryInstanceMutation()

  const removePartFromCart = useCallback(
    (inventoryInstanceEntry: InventoryPartInstanceEntry) => {
      updateInventoryInstance({
        authorization: accessToken,
        inventoryPartInstanceEntry: {
          id: inventoryInstanceEntry.id,
          info: {
            ...inventoryInstanceEntry.info,
            claimedByCounterSale: undefined,
            counterSalePricePerUnit: undefined
          }
        }
      })
    },
    [accessToken, updateInventoryInstance]
  )

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      spacing={2}>
      {(counterSale?.pricing?.inventoryPartEntries || []).map(
        (inventoryInstanceEntry) => (
          <Paper
            key={inventoryInstanceEntry.id}
            variant="outlined"
            sx={{ width: '100%' }}>
            <Box padding={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between">
                <Typography variant="h6">
                  <b>
                    {
                      allShopInventory?.allParts![
                        inventoryInstanceEntry.info?.inventoryPartId!
                      ]?.info?.name
                    }
                  </b>
                </Typography>
                {!readOnly && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CounterSaleEditInventoryInCart
                      instance={inventoryInstanceEntry}
                    />
                    <IconButton
                      onClick={() =>
                        removePartFromCart(inventoryInstanceEntry)
                      }>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                )}
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between">
                <Typography variant="h6">
                  {formatStringToTwoDecimalPlaces(
                    inventoryInstanceEntry.info?.quantity + ''
                  )}
                </Typography>
                <Typography variant="caption">
                  {`Unit. ${formatCurrency(
                    inventoryInstanceEntry.info?.counterSalePricePerUnit || 0,
                    true
                  )}`}
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(
                    (inventoryInstanceEntry.info?.counterSalePricePerUnit ||
                      0) * (inventoryInstanceEntry.info?.quantity || 0),
                    true
                  )}
                </Typography>
              </Stack>
            </Box>
          </Paper>
        )
      )}
    </Stack>
  )
}

export default CounterSaleCartInventory
